import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiService from "../services/apiService";
import { formatDifficulty, getDifficultyClass } from "../utils/formatters";
import AIDisclaimer from "../components/AIDisclaimer";
import ExportModal from "../components/ExportModal";
import MathText from "../components/MathText";
import "./css/QuestionSetDetail.css";

function QuestionSetDetail() {
  const { questionSetId } = useParams();
  const navigate = useNavigate();
  const [questionSet, setQuestionSet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewingQuestion, setReviewingQuestion] = useState(null);
  const [selectedRating, setSelectedRating] = useState(null);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [exportingToAQMS, setExportingToAQMS] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    async function fetchQuestionSetDetails() {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getQuestionSetById(questionSetId);
        setQuestionSet(data);
      } catch (err) {
        setError("Failed to load question set details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchQuestionSetDetails();
  }, [questionSetId]);

  const handleReview = async (questionId, rating) => {
    setSubmittingReview(true);
    try {
      await apiService.submitQuestionReview(questionId, rating, reviewComment || null);
      
      const updatedData = await apiService.getQuestionSetById(questionSetId);
      setQuestionSet(updatedData);
      
      setReviewingQuestion(null);
      setSelectedRating(null);
      setReviewComment("");
      
      alert("Review submitted successfully!");
    } catch (err) {
      alert(`Failed to submit review: ${err.message}`);
      console.error(err);
    } finally {
      setSubmittingReview(false);
    }
  };

  const startReview = (questionId, rating) => {
    setReviewingQuestion(questionId);
    setSelectedRating(rating);
    setReviewComment("");
  };

  const cancelReview = () => {
    setReviewingQuestion(null);
    setSelectedRating(null);
    setReviewComment("");
  };

  const handleEditQuestion = (questionId) => {
    navigate(`/questions/${questionId}/edit`);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteQuestion(questionId);
      
      // Refresh the question set after deletion
      const updatedData = await apiService.getQuestionSetById(questionSetId);
      setQuestionSet(updatedData);
      
      alert('Question deleted successfully!');
    } catch (err) {
      alert(`Failed to delete question: ${err.message}`);
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const convertToCSV = (questions) => {
    if (!questions || questions.length === 0) return '';

    // Apr 30 fix: previously hardcoded 4 option columns so 5- and 6-option
    // papers lost data and "Correct Answer" came out blank. Now compute the
    // max option count across this set (clamped 4..6) so every option survives
    // the round trip.
    let maxOpts = 0;
    for (const q of questions) {
      const n = (q.options || []).length;
      if (n > maxOpts) maxOpts = n;
    }
    maxOpts = Math.min(Math.max(maxOpts, 4), 6);

    const optHeaders = Array.from({ length: maxOpts }, (_, i) => `Option ${String.fromCharCode(65 + i)}`);
    const headers = ['Question', 'Type', ...optHeaders, 'Correct Answer', 'Difficulty', 'Blooms Taxonomy', 'Outcomes'];

    const rows = questions.map(q => {
      const options = q.options || [];
      const correctOption = q.correct_option;
      const optCells = Array.from({ length: maxOpts }, (_, i) =>
        `"${options[i]?.text?.replace(/"/g, '""') || ''}"`
      );

      return [
        `"${q.text?.replace(/"/g, '""') || ''}"`,
        q.type || '',
        ...optCells,
        `"${correctOption?.text?.replace(/"/g, '""') || q.answer?.replace(/"/g, '""') || ''}"`,
        formatDifficulty(q.difficulty),
        q.blooms_taxonomy || '',
        `"${q.outcomes?.map(o => o.short_name).join(', ') || ''}"`
      ].join(',');
    });

    return [headers.join(','), ...rows].join('\n');
  };

  const handleDownloadCSV = () => {
    if (!questionSet || !questionSet.questions) {
      alert('No questions to download');
      return;
    }

    const csv = convertToCSV(questionSet.questions);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${questionSet.name || 'question-set'}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadExcel = async () => {
    try {
      const res = await apiService.downloadQuestionSetExcel(questionSetId);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${questionSet?.name || 'question_set'}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download failed: ' + e.message);
    }
  };

  const handleExportToAQMS = async () => {
    setShowExportModal(true);
  };

  const handleConfirmExport = async (selectedQuestionIds) => {
    setExportingToAQMS(true);
    try {
      await apiService.exportQuestionSetToAQMS(questionSetId, selectedQuestionIds);
      const count = selectedQuestionIds ? selectedQuestionIds.length : questionSet.questions.length;
      toast.success(`Successfully exported ${count} question${count !== 1 ? 's' : ''} to AQMS!`);
      setShowExportModal(false);
    } catch (err) {
      toast.error(`Failed to export to AQMS: ${err.message}`);
      console.error(err);
    } finally {
      setExportingToAQMS(false);
    }
  };

  const renderQuestion = (question, index) => {
    const isReviewing = reviewingQuestion === question.id;

    return (
      <div key={question.id} className="lesson-card">
        <div className="question-card-header">
          <h4 className="question-title">Question {index + 1}</h4>
          <div className="question-badges">
            <span className={`badge ${getDifficultyClass(question.difficulty)}`}>
              {formatDifficulty(question.difficulty)}
            </span>
            <span className="badge badge-taxonomy">
              {question.blooms_taxonomy || 'N/A'}
            </span>
            {question.outcomes && question.outcomes.length > 0 && (
              <span className="badge badge-outcomes">
                {question.outcomes.map(o => o.short_name).join(', ')}
              </span>
            )}
          </div>
        </div>

        <MathText className="question-text">{question.text}</MathText>

        {question.type === 'multiple_choice' && question.options && (
          <div className="options-section">
            <h5 className="options-title">Options:</h5>
            <div className="options-list">
              {question.options.map((option) => (
                <div 
                  key={option.id} 
                  className={`option-item ${option.is_correct ? 'option-correct' : 'option-incorrect'}`}
                >
                  {option.is_correct && <span className="option-check">✓</span>}
                  <MathText className="option-text">{option.text}</MathText>
                </div>
              ))}
            </div>
          </div>
        )}

        {question.answer && (
          <div className="answer-section">
            <h5 className="answer-title">Answer:</h5>
            <MathText className="answer-text">{question.answer}</MathText>
          </div>
        )}

        <div className="question-actions">
          <button
            className="btn btn-outline edit-question-button"
            onClick={() => handleEditQuestion(question.id)}
          >
            ✏️ Edit Question
          </button>
          <button
            className="btn btn-danger delete-question-button"
            onClick={() => handleDeleteQuestion(question.id)}
          >
            🗑️ Delete Question
          </button>
        </div>

        <div className="review-section">
          <div className="review-header">
            <h5 className="review-title">Question Review</h5>
            {question.review_summary && (
              <div className="review-stats">
                <span>👍 {question.review_summary.thumbs_up}</span>
                <span>👎 {question.review_summary.thumbs_down}</span>
                <span>({question.review_summary.total_reviews} reviews)</span>
              </div>
            )}
          </div>

          {!isReviewing ? (
            <div className="review-buttons">
              <button
                className="btn btn-success review-button"
                onClick={() => startReview(question.id, 'thumbs_up')}
              >
                👍 Thumbs Up
              </button>
              <button
                className="btn btn-danger review-button"
                onClick={() => startReview(question.id, 'thumbs_down')}
              >
                👎 Thumbs Down
              </button>
            </div>
          ) : (
            <div className="review-form">
              <textarea
                placeholder="Add an optional comment about this question..."
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                className="review-textarea"
              />
              <div className="review-submit-buttons">
                <button
                  className={`btn ${selectedRating === 'thumbs_up' ? 'btn-success' : 'btn-danger'} review-submit-button`}
                  onClick={() => handleReview(question.id, selectedRating)}
                  disabled={submittingReview}
                >
                  {submittingReview ? "Submitting..." : `${selectedRating === 'thumbs_up' ? '👍' : '👎'} Submit ${selectedRating === 'thumbs_up' ? 'Thumbs Up' : 'Thumbs Down'}`}
                </button>
                <button
                  className="btn btn-secondary review-cancel-button"
                  onClick={cancelReview}
                  disabled={submittingReview}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) return <div>Loading question set...</div>;
  if (error) return <div>{error}</div>;
  if (!questionSet) return <div>No question set found.</div>;

  return (
    <div>
      <div className="course-detail-header question-set-detail-header">
        <div>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1 className="question-set-header">{questionSet.name}</h1>
          <div className="question-set-meta">
            <span>📝 {questionSet.question_count} Questions</span>
            <span>📋 {questionSet.answer_style?.replace('_', ' ').toUpperCase()}</span>
            <span>📅 Created: {formatDate(questionSet.created_at)}</span>
          </div>
        </div>
        <div className="header-buttons">
          <button
            className="btn btn-primary download-button"
            onClick={handleDownloadCSV}
          >
            📥 Download CSV
          </button>
          <button
            className="btn download-button"
            style={{ background: '#28a745', color: '#fff' }}
            onClick={handleDownloadExcel}
          >
            📥 Download Excel
          </button>
          <button
            className="btn btn-success"
            onClick={handleExportToAQMS}
            disabled={!questionSet.questions || questionSet.questions.length === 0}
          >
            📤 Export To AQMS
          </button>
          <button
            className="btn"
            style={{ background: '#042F54', color: '#fff' }}
            onClick={() => navigate(`/audit-results/qs/${questionSetId}`)}
          >
            Run Audit
          </button>
        </div>
      </div>

      <div id="course-detail-tab-content" className="question-set-content">
        <AIDisclaimer message="These questions were generated by AI. Please review and verify accuracy before using them." />
        {questionSet.questions && questionSet.questions.length > 0 ? (
          questionSet.questions.map((question, index) => renderQuestion(question, index))
        ) : (
          <p className="no-questions-message">No questions found in this set.</p>
        )}
      </div>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        questions={questionSet.questions || []}
        onExport={handleConfirmExport}
        isExporting={exportingToAQMS}
      />
    </div>
  );
}

export default QuestionSetDetail;